import { useState, useEffect } from "react";
import axios from "axios";

export default function ScoutingMaintenance({ onBack, onSelect, selected_plant_id }) {
  const [selectedDropdownValue, setSelectedDropdownValue] = useState("");
  const [selectedSeverityLevel, setSelectedSeverityLevel] = useState("");
  const [selectedAlarmLevel, setSelectedAlarmLevel] = useState("");
  const [otherDescription, setOtherDescription] = useState("");
  const [nutrientType, setNutrientType] = useState("");
  const [plantCount, setPlantCount] = useState(0);
  
  // Lookup data state
  const [pestTypes, setPestTypes] = useState([]);
  const [diseaseTypes, setDiseaseTypes] = useState([]);
  const [mutationTypes, setMutationTypes] = useState([]);
  const [nutrientElements, setNutrientElements] = useState([]);
  const [stressorTypes, setStressorTypes] = useState([]);

  // Check if we have multiple plants (from a plot)
  useEffect(() => {
    if (Array.isArray(selected_plant_id)) {
      setPlantCount(selected_plant_id.length);
      console.log(`Found ${selected_plant_id.length} plants for scouting maintenance`);
    }
  }, [selected_plant_id]);

  /* local state that tracks which dropdowns are open ----------------- */
  const [openSubs, setOpenSubs] = useState(new Set());
  const toggle = (key) =>
    setOpenSubs((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  /* helper: tiny text next to the header to show current selection ---- */
  const summary = {
    DropdownValue: selectedDropdownValue,
  };
  
  const org_id = localStorage.getItem("organization");
  
  // Fetch lookup data when component mounts
  useEffect(() => {
    const fetchLookupData = async () => {
      try {
        const endpoints = {
          'pest_type': setPestTypes,
          'disease_type': setDiseaseTypes,
          'mutation_type': setMutationTypes,
          'nutrient_element': setNutrientElements,
          'stressor_type': setStressorTypes
        };

        for (const [endpoint, setter] of Object.entries(endpoints)) {
          const response = await axios.get(`/orgs/${org_id}/lookups/${endpoint}?locale=en`, { 
            withCredentials: true 
          });
          setter(response.data);
        }
      } catch (error) {
        console.error("Error fetching lookup data:", error);
      }
    };

    fetchLookupData();
  }, [org_id]);

  const handleUpload = async () => {
    // Get the currently active category
    let category = "";
    let data = {};
    
    if (openSubs.has("pests")) {
      category = "pest";
      data = { pest_type_id: parseInt(selectedDropdownValue) };
    } else if (openSubs.has("diseases")) {
      category = "disease";
      data = { disease_type_id: parseInt(selectedDropdownValue) };
    } else if (openSubs.has("mutations")) {
      category = "mutation";
      data = { mutation_type_id: parseInt(selectedDropdownValue) };
    } else if (openSubs.has("nutrientImbalance")) {
      category = "nutrient_imbalance";
      data = { 
        nutrient_element_id: parseInt(selectedDropdownValue),
        toxicity_or_deficiency: nutrientType
      };
    } else if (openSubs.has("environmentalStressor")) {
      category = "environmental_stressor";
      data = { stressor_type_id: parseInt(selectedDropdownValue) };
    } else if (openSubs.has("other")) {
      category = "other";
      data = { notes: otherDescription };
    } else {
      alert("Please select a category");
      return;
    }
    
    if (!selectedDropdownValue && category !== "other") {
      alert("Please select a value");
      return;
    }
    
    if (category === "other" && !otherDescription) {
      alert("Please enter a description");
      return;
    }
    
    if (!selectedSeverityLevel) {
      alert("Please select a severity level");
      return;
    }
    
    if (!selectedAlarmLevel) {
      alert("Please select an alarm level");
      return;
    }
    
    // Create array of plant IDs
    const plantIds = Array.isArray(selected_plant_id) ? selected_plant_id : [selected_plant_id];
    
    try {
      // Send a single request with the array of plant IDs
      const payload = {
        plant_id: plantIds,
        type: category,
        severity: parseInt(selectedSeverityLevel),
        alarm_level: selectedAlarmLevel.toLowerCase(),
        ...data
      };
      
      await axios.post(`/orgs/${org_id}/maintenance/scouting`, payload, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" }
      });
      
      alert("Upload complete");
      onBack && onBack();
    } catch (err) {
      console.error("Upload failed:", err);
      if (err.response?.status === 400) {
                alert('Upload failed - Check if all required fields are filled in correctly');
            } else if (err.response?.status === 401 || err.response?.status === 403) {
                alert("Upload failed - You are no longer logged in or you don't have access to this resource");
            } else if (err.response?.status === 500) {
                alert("Upload failed - Something went wrong in the service, please try again later");
            } else {
                alert(`Upload failed with error: ${err.response}`);
            }
    }
  };

  /* ------------------------------------------------------------------ */
  return (
    <>
        {/* header & back -------------------------------------------------- */}
        <div className="maintenanceHeader">
            <h3>Scouting</h3>
            <button className="uploadMaintenanceBtn" onClick={handleUpload}>Upload</button>
        </div>

        <div className="maintenaceGrid maintenanceList">
        <div className="dropdownBlock">
            <div
                className="purpleBtn extraGridBtn blueBtn"
                onClick={() =>
                    openSubs.size > 0 ? setOpenSubs(new Set()) : onBack()
                }
            >
                {openSubs.size > 0 ? "Back to Categories" : "Back"}
            </div>
            </div>
            
            {plantCount > 1 && (
              <div className="plotMaintenanceNotice">
                Applying scouting maintenance to all plants in plot ({plantCount} plants)
              </div>
            )}
        </div>

        <div className="maintenaceGrid maintenanceList flexMaintenanceGrid">
            {!openSubs.has("pests") &&
            !openSubs.has("diseases") &&
            !openSubs.has("mutations") &&
            !openSubs.has("nutrientImbalance") && 
            !openSubs.has("environmentalStressor") &&
            !openSubs.has("other") ? (
                <>
                    <div className="purpleBox" onClick={() => toggle("pests")}>
                        Pests
                    </div>
                    <div className="purpleBox" onClick={() => toggle("diseases")}>
                        Diseases
                    </div>
                    <div className="purpleBox" onClick={() => toggle("mutations")}>
                        Mutations
                    </div>
                    <div
                        className="purpleBox"
                        onClick={() => toggle("nutrientImbalance")}
                    >
                        Nutrient Imbalance
                    </div>
                    <div
                        className="purpleBox"
                        onClick={() => toggle("environmentalStressor")}
                    >
                        Environmental Stressor
                    </div>
                    <div className="purpleBox" onClick={() => toggle("other")}>
                        Other
                    </div>
                </>
            ) : openSubs.has("pests") ? (
                <table className="maintenanceTable">
                    <tbody>
                        <tr>
                            <td>
                                <p>Pest</p>
                            </td>
                            <td>
                                <select
                                    onChange={(e) => setSelectedDropdownValue(e.target.value)}
                                    className="newMaintenanceInput"
                                >
                                    <option value="">Select a pest</option>
                                    {pestTypes.map((pest) => (
                                        <option key={pest.id} value={pest.id}>
                                            {pest.name}
                                        </option>
                                    ))}
                                </select>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>Severity Level</p>
                            </td>
                            <td>
                                <div className="severity">
                                    {[1, 2, 3, 4, 5].map((level) => (
                                        <label key={level}>
                                            <input
                                                type="radio"
                                                name="severity"
                                                className="tableRadio tableRadioScouting"
                                                value={level}
                                                onChange={(e) => setSelectedSeverityLevel(e.target.value)}
                                            />
                                            {level}
                                        </label>
                                    ))}
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>Alarm Level</p>
                            </td>
                            <td>
                                <div className="alarm">
                                    {["Low", "Medium", "High"].map((level) => (
                                        <label key={level}>
                                            <input
                                                type="radio"
                                                name="alarm"
                                                className="tableRadio tableRadioScouting"
                                                value={level}
                                                onChange={(e) => setSelectedAlarmLevel(e.target.value)}
                                            />
                                            {level}
                                        </label>
                                    ))}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            ) : openSubs.has("diseases") ? (
                <table className="maintenanceTable">
                    <tbody>
                        <tr>
                            <td>
                                <p>Disease</p>
                            </td>
                            <td>
                                <select
                                    onChange={(e) => setSelectedDropdownValue(e.target.value)}
                                    className="newMaintenanceInput"
                                >
                                    <option value="">Select a disease</option>
                                    {diseaseTypes.map((disease) => (
                                        <option key={disease.id} value={disease.id}>
                                            {disease.name}
                                        </option>
                                    ))}
                                </select>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>Severity Level</p>
                            </td>
                            <td>
                                <div className="severity">
                                    {[1, 2, 3, 4, 5].map((level) => (
                                        <label key={level}>
                                            <input
                                                type="radio"
                                                name="severity"
                                                className="tableRadio tableRadioScouting"
                                                value={level}
                                                onChange={(e) => setSelectedSeverityLevel(e.target.value)}
                                            />
                                            {level}
                                        </label>
                                    ))}
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>Alarm Level</p>
                            </td>
                            <td>
                                <div className="alarm">
                                    {["Low", "Medium", "High"].map((level) => (
                                        <label key={level}>
                                            <input
                                                type="radio"
                                                name="alarm"
                                                className="tableRadio tableRadioScouting"
                                                value={level}
                                                onChange={(e) => setSelectedAlarmLevel(e.target.value)}
                                            />
                                            {level}
                                        </label>
                                    ))}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            ) : openSubs.has("mutations") ? (
                <table className="maintenanceTable">
                    <tbody>
                        <tr>
                            <td>
                                <p>Mutation</p>
                            </td>
                            <td>
                                <select
                                    onChange={(e) => setSelectedDropdownValue(e.target.value)}
                                    className="newMaintenanceInput"
                                >
                                    <option value="">Select a mutation</option>
                                    {mutationTypes.map((mutation) => (
                                        <option key={mutation.id} value={mutation.id}>
                                            {mutation.name}
                                        </option>
                                    ))}
                                </select>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>Severity Level</p>
                            </td>
                            <td>
                                <div className="severity">
                                    {[1, 2, 3, 4, 5].map((level) => (
                                        <label key={level}>
                                            <input
                                                type="radio"
                                                name="severity"
                                                className="tableRadio tableRadioScouting"
                                                value={level}
                                                onChange={(e) => setSelectedSeverityLevel(e.target.value)}
                                            />
                                            {level}
                                        </label>
                                    ))}
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>Alarm Level</p>
                            </td>
                            <td>
                                <div className="alarm">
                                    {["Low", "Medium", "High"].map((level) => (
                                        <label key={level}>
                                            <input
                                                type="radio"
                                                name="alarm"
                                                className="tableRadio tableRadioScouting"
                                                value={level}
                                                onChange={(e) => setSelectedAlarmLevel(e.target.value)}
                                            />
                                            {level}
                                        </label>
                                    ))}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            ) : openSubs.has("nutrientImbalance") ? (
                <table className="maintenanceTable">
                    <tbody>
                        <tr>
                            <td>
                                <p>Nutrient</p>
                            </td>
                            <td>
                                <select
                                    onChange={(e) => setSelectedDropdownValue(e.target.value)}
                                    className="newMaintenanceInput"
                                >
                                    <option value="">Select a nutrient</option>
                                    {nutrientElements.map((element) => (
                                        <option key={element.id} value={element.id}>
                                            {element.name}
                                        </option>
                                    ))}
                                </select>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>Type</p>
                            </td>
                            <td>
                                <div className="nutrientType">
                                    <label>
                                        <input
                                            type="radio"
                                            name="nutrientType"
                                            className="tableRadio tableRadioScouting"
                                            value="deficiency"
                                            onChange={(e) => setNutrientType(e.target.value)}
                                        />
                                        Deficiency
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="nutrientType"
                                            className="tableRadio tableRadioScouting"
                                            value="toxicity"
                                            onChange={(e) => setNutrientType(e.target.value)}
                                        />
                                        Toxicity
                                    </label>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>Severity Level</p>
                            </td>
                            <td>
                                <div className="severity">
                                    {[1, 2, 3, 4, 5].map((level) => (
                                        <label key={level}>
                                            <input
                                                type="radio"
                                                name="severity"
                                                className="tableRadio tableRadioScouting"
                                                value={level}
                                                onChange={(e) => setSelectedSeverityLevel(e.target.value)}
                                            />
                                            {level}
                                        </label>
                                    ))}
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>Alarm Level</p>
                            </td>
                            <td>
                                <div className="alarm">
                                    {["Low", "Medium", "High"].map((level) => (
                                        <label key={level}>
                                            <input
                                                type="radio"
                                                name="alarm"
                                                className="tableRadio tableRadioScouting"
                                                value={level}
                                                onChange={(e) => setSelectedAlarmLevel(e.target.value)}
                                            />
                                            {level}
                                        </label>
                                    ))}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            ) : openSubs.has("environmentalStressor") ? (
                    <table className="maintenanceTable">
                            <tbody>
                                    <tr>
                                            <td>
                                                    <p>Stressor</p>
                                            </td>
                                            <td>
                                                    <select
                                                            onChange={(e) => setSelectedDropdownValue(e.target.value)}
                                                            className="newMaintenanceInput"
                                                    >
                                                            <option value="">Select a stressor</option>
                                                            {stressorTypes.map((stressor) => (
                                                                <option key={stressor.id} value={stressor.id}>
                                                                    {stressor.name}
                                                                </option>
                                                            ))}
                                                    </select>
                                            </td>
                                    </tr>
                                    <tr>
                                            <td>
                                                    <p>Severity Level</p>
                                            </td>
                                            <td>
                                                    <div className="severity">
                                                            {[1, 2, 3, 4, 5].map((level) => (
                                                                    <label key={level}>
                                                                            <input
                                                                                    type="radio"
                                                                                    name="severity"
                                                                                    className="tableRadio tableRadioScouting"
                                                                                    value={level}
                                                                                    onChange={(e) => setSelectedSeverityLevel(e.target.value)}
                                                                            />
                                                                            {level}
                                                                    </label>
                                                            ))}
                                                    </div>
                                            </td>
                                    </tr>
                                    <tr>
                                            <td>
                                                    <p>Alarm Level</p>
                                            </td>
                                            <td>
                                                    <div className="alarm">
                                                            {["Low", "Medium", "High"].map((level) => (
                                                                    <label key={level}>
                                                                            <input
                                                                                    type="radio"
                                                                                    name="alarm"
                                                                                    className="tableRadio tableRadioScouting"
                                                                                    value={level}
                                                                                    onChange={(e) => setSelectedAlarmLevel(e.target.value)}
                                                                            />
                                                                            {level}
                                                                    </label>
                                                            ))}
                                                    </div>
                                            </td>
                                    </tr>
                            </tbody>
                    </table>
            ) : openSubs.has("other") ? (
                    <table className="maintenanceTable">
                            <tbody>
                                    <tr>
                                            <td>
                                                    <p>Description</p>
                                            </td>
                                            <td>
                                                    <textarea
                                                            className="newMaintenanceInput"
                                                            placeholder="Enter description"
                                                            onChange={(e) => setOtherDescription(e.target.value)}
                                                    />
                                            </td>
                                    </tr>
                                    <tr>
                                            <td>
                                                    <p>Severity Level</p>
                                            </td>
                                            <td>
                                                    <div className="severity">
                                                            {[1, 2, 3, 4, 5].map((level) => (
                                                                    <label key={level}>
                                                                            <input
                                                                                    type="radio"
                                                                                    name="severity"
                                                                                    className="tableRadio tableRadioScouting"
                                                                                    value={level}
                                                                                    onChange={(e) => setSelectedSeverityLevel(e.target.value)}
                                                                            />
                                                                            {level}
                                                                    </label>
                                                            ))}
                                                    </div>
                                            </td>
                                    </tr>
                                    <tr>
                                            <td>
                                                    <p>Alarm Level</p>
                                            </td>
                                            <td>
                                                    <div className="alarm">
                                                            {["Low", "Medium", "High"].map((level) => (
                                                                    <label key={level}>
                                                                            <input
                                                                                    type="radio"
                                                                                    name="alarm"
                                                                                    className="tableRadio tableRadioScouting"
                                                                                    value={level}
                                                                                    onChange={(e) => setSelectedAlarmLevel(e.target.value)}
                                                                            />
                                                                            {level}
                                                                    </label>
                                                            ))}
                                                    </div>
                                            </td>
                                    </tr>
                            </tbody>
                    </table>
            ) : null}
        </div>
    </>
  );
}
